import { zRsvpInput } from '@axorain/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { getMyRsvp, submitRsvp } from '../api/rsvp';
import { useAuth } from '../auth/useAuth';
import { Screen } from '../components/Screen';
import { Button, ErrorText, TextArea } from '../components/ui';

type Attending = 'yes' | 'no';

/** Two-button segmented control for the yes/no attending choice. */
function Segmented({ value, onChange }: { value: Attending; onChange: (v: Attending) => void }) {
  const opts: { v: Attending; label: string }[] = [
    { v: 'yes', label: 'Joyfully accepts' },
    { v: 'no', label: 'Regretfully declines' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
      {opts.map(({ v, label }) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            style={{
              padding: '14px 10px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? 'var(--white)' : 'rgba(255,255,255,0.05)',
              color: active ? '#0a0a0a' : 'var(--text)',
              border: `1px solid ${active ? 'transparent' : 'var(--border)'}`,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** −/＋ stepper for guest count (1–10 when attending). */
function Stepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const clamp = (n: number) => Math.min(10, Math.max(1, n));
  const btn = {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--border)',
    color: 'var(--text)',
    fontSize: 22,
    cursor: 'pointer',
  } as const;
  return (
    <div style={{ marginBottom: 18 }}>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
        How many of you, total?
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          type="button"
          aria-label="One fewer"
          style={btn}
          onClick={() => onChange(clamp(value - 1))}
        >
          −
        </button>
        <span style={{ fontSize: 20, fontWeight: 600, minWidth: 24, textAlign: 'center' }}>
          {value}
        </span>
        <button
          type="button"
          aria-label="One more"
          style={btn}
          onClick={() => onChange(clamp(value + 1))}
        >
          ＋
        </button>
      </div>
    </div>
  );
}

export function Register() {
  const { profile } = useAuth();
  const [attending, setAttending] = useState<Attending>('yes');
  const [guestCount, setGuestCount] = useState(1);
  const [dietary, setDietary] = useState('');
  const [note, setNote] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // We don't block the form on the fetch — the form renders instantly and we prefill
  // quietly when a saved RSVP arrives. `editedRef` guards against the (cold-start-slow)
  // response overwriting edits the guest already started making.
  const editedRef = useRef(false);

  useEffect(() => {
    let live = true;
    getMyRsvp()
      .then((rsvp) => {
        if (!live || !rsvp || editedRef.current) return;
        setAttending(rsvp.attending);
        setGuestCount(rsvp.attending === 'yes' ? Math.max(1, rsvp.guestCount) : 1);
        setDietary(rsvp.dietary);
        setNote(rsvp.note);
        setSaved(true);
      })
      .catch(() => undefined); // first-time guest (404) or transient — leave the blank form
    return () => {
      live = false;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    // Validate against the same shared contract the API enforces.
    const parsed = zRsvpInput.safeParse({
      attending,
      guestCount: attending === 'yes' ? guestCount : 0,
      dietary,
      note,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please check the form.');
      return;
    }
    setBusy(true);
    try {
      await submitRsvp(parsed.data);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Any edit clears the "saved" confirmation and stops a late prefill clobbering it.
  const touched = () => {
    editedRef.current = true;
    setSaved(false);
  };

  const firstName = profile?.name?.split(' ')[0] ?? '';

  return (
    <Screen>
      <h1 className="serif" style={{ fontSize: '2rem', fontWeight: 500, margin: '0 0 4px' }}>
        Will you join us?
      </h1>
      <p style={{ color: 'var(--text-dim)', fontSize: 14, margin: '0 0 6px' }}>
        {firstName ? `${firstName}, we’d` : 'We’d'} love to know if you can make it.
      </p>
      {saved ? (
        <p
          style={{
            color: '#7fd6a8',
            fontSize: 13,
            margin: '0 0 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          ✓ Your RSVP is saved — you can change it any time.
        </p>
      ) : (
        <div style={{ height: 14 }} />
      )}

      <form onSubmit={onSubmit}>
        <Segmented
          value={attending}
          onChange={(v) => {
            setAttending(v);
            touched();
          }}
        />

        {attending === 'yes' && (
          <Stepper
            value={guestCount}
            onChange={(n) => {
              setGuestCount(n);
              touched();
            }}
          />
        )}

        {attending === 'yes' && (
          <TextArea
            label="Any dietary needs? (optional)"
            placeholder="e.g. vegetarian, no nuts, halal"
            maxLength={280}
            value={dietary}
            onChange={(e) => {
              setDietary(e.target.value);
              touched();
            }}
          />
        )}

        <TextArea
          label="A note for the couple (optional)"
          placeholder={attending === 'yes' ? 'We can’t wait! 🎉' : 'Wishing you both the best 💛'}
          maxLength={500}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            touched();
          }}
        />

        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : saved ? 'Update my RSVP' : 'Send RSVP'}
        </Button>
      </form>
    </Screen>
  );
}
